import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { sendFissalChat } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import { ContextBreadcrumb } from '../components/ContextBreadcrumb';
import { Image, ExternalLink, RefreshCw, ShieldCheck, HardDrive, Smartphone, CheckCircle, Sparkles } from 'lucide-react';

export const FamilyPhotoVaultView: React.FC = () => {
  const { refreshStatus, setActiveTab } = useApp();
  const [healing, setHealing] = useState(false);
  const [healMessage, setHealMessage] = useState<string | null>(null);

  const handleFixGlitch = async () => {
    setHealing(true);
    setHealMessage(null);
    triggerHaptic([30, 40, 30]);
    try {
      const res = await sendFissalChat('Please fix_household_glitch for photos', [], 'family');
      setHealMessage(res.answer || 'Photo Vault refreshed successfully!');
      await refreshStatus();
    } catch (e: any) {
      setHealMessage(`Refresh failed: ${e.message}`);
    } finally {
      setHealing(false);
      setTimeout(() => setHealMessage(null), 8000);
    }
  };

  const steps = [
    {
      step: '1',
      title: 'Get the Immich App',
      desc: 'Download the official Immich app from the Apple App Store (iPhone) or Google Play Store (Android).'
    },
    {
      step: '2',
      title: 'Enter Server Address',
      desc: 'When asked for the server URL, type: https://photos.ech-o.net (or http://192.168.68.50:2283 on home Wi-Fi).'
    },
    {
      step: '3',
      title: 'Log In to Family Account',
      desc: 'Sign in with your The Relay family account credentials.'
    },
    {
      step: '4',
      title: 'Enable Auto-Backup',
      desc: 'Tap the cloud icon or Settings > Backup, and toggle ON automatic backup for your camera roll and albums.'
    }
  ];

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4">
      <ContextBreadcrumb realmName="Family Realm" deckTitle="Immich Photo Vault" realmEmoji="📸" />
      {/* Photo Vault Hero Header */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-950/50 via-slate-900/80 to-slate-950 border border-emerald-500/30 shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300">
              <Image className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-cinzel text-sm font-bold text-emerald-300 tracking-wider">
                IMMICH PHOTO VAULT
              </h2>
              <p className="text-[11px] text-slate-300">
                The Relay Sovereign Memory Bank (Port 2283)
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/15 px-2.5 py-1 rounded-full border border-emerald-500/30 font-semibold flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Protected
          </span>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed mt-2">
          Your full-quality original photos and videos are stored safely right here at home on The Relay hardware, never on big tech corporate clouds.
        </p>

        <div className="flex items-center gap-2 pt-3">
          <a
            href="https://photos.ech-o.net"
            target="_blank"
            rel="noreferrer"
            className="flex-1 py-2.5 px-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 shadow-verdant-glow transition-all"
          >
            <ExternalLink className="w-4 h-4" /> Open Photo Vault
          </a>

          <button
            onClick={handleFixGlitch}
            disabled={healing}
            className="py-2.5 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-slate-300 hover:text-white flex items-center gap-1.5 transition-all disabled:opacity-50"
            title="Gently refresh photo server if stuck"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${healing ? 'animate-spin text-emerald-400' : ''}`} />
            <span>{healing ? 'Healing...' : 'Refresh Vault'}</span>
          </button>
        </div>

        {healMessage && (
          <div className="mt-3 p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle className="w-4 h-4 shrink-0 text-emerald-400" />
            <span>{healMessage}</span>
          </div>
        )}
      </div>

      {/* Storage & Hardware Status */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-white">
          <HardDrive className="w-4 h-4 text-emerald-400" />
          <span>Sovereign Storage Architecture</span>
        </div>
        <div className="bg-black/40 rounded-xl p-2.5 text-[11px] text-slate-300 space-y-1 font-mono border border-white/5">
          <div className="flex justify-between">
            <span className="text-slate-400">Storage Location:</span>
            <span className="text-emerald-300">/srv/samba/ConPhotos</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Dedicated Host:</span>
            <span className="text-slate-200">echodedicated (Port 2283)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Cloud Rent:</span>
            <span className="text-emerald-400 font-bold">$0.00 / Zero Cloud Fees</span>
          </div>
        </div>
      </div>

      {/* Step-by-Step Mobile Backup Guide */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-white">
            <Smartphone className="w-4 h-4 text-sky-400" />
            <span>How to Back Up Photos From Your Phone</span>
          </div>
          <span className="text-[10px] text-emerald-400 font-mono">Simple 4 Steps</span>
        </div>

        <div className="space-y-2.5">
          {steps.map((s) => (
            <div key={s.step} className="flex items-start gap-2.5 bg-black/30 p-2.5 rounded-xl border border-white/5">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                {s.step}
              </span>
              <div>
                <h4 className="text-xs font-semibold text-white">{s.title}</h4>
                <p className="text-[11px] text-slate-300 leading-relaxed mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setActiveTab('concierge')}
          className="w-full py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-slate-300 hover:text-white flex items-center justify-center gap-1.5 transition-all mt-2"
        >
          <Sparkles className="w-3.5 h-3.5 text-fissal-gold" />
          <span>Ask Fissal more about photo backups</span>
        </button>
      </div>
    </div>
  );
};
